import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getActiveTemplates } from "@/services/templates";

interface CardTemplate {
  id: string;
  name: string;
  image_url: string;
  occasion: string;
}

const FILTERS: { key: string; value: string | null }[] = [
  { key: "all", value: null },
  { key: "birthday", value: "birthday" },
  { key: "farewell", value: "farewell" },
  { key: "baby", value: "baby" },
  { key: "thankYou", value: "thank-you" },
  { key: "teacher", value: "teacher" },
  { key: "getWell", value: "get-well" },
];

const CardsBrowser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState<string | null>(null);
  const [templates, setTemplates] = useState<CardTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getActiveTemplates(active ?? undefined).then(({ data }) => {
      if (cancelled) return;
      setTemplates(data as CardTemplate[]);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return (
    <section id="cards" className="py-20 md:py-28 bg-background border-t border-border/40">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-5xl font-extralight text-foreground tracking-tight">
            {t("cardsBrowser.title", "Find a card for the occasion")}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg mx-auto text-base font-light">
            {t("cardsBrowser.subtitle", "Pick a design, rally the group, and send something they'll keep.")}
          </p>
        </motion.div>

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mx-4 px-4 scrollbar-none">
          {FILTERS.map((f) => {
            const isActive = active === f.value;
            return (
              <button
                key={f.key}
                onClick={() => setActive(f.value)}
                className={`shrink-0 rounded-full px-5 py-2 text-xs font-light tracking-wide transition-colors border ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground border-border/60 hover:border-foreground/30"
                }`}
              >
                {t(`occasions.${f.key}`, f.key === "all" ? "All" : f.key)}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm font-light">
            {t("cardsBrowser.empty", "No cards yet for this occasion.")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => navigate("/create")}
                className="group text-start"
              >
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-border/60 bg-muted mb-3 transition-shadow group-hover:shadow-lg">
                  <img
                    src={tpl.image_url}
                    alt={tpl.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="text-xs font-light text-foreground tracking-wide">
                  {tpl.name}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CardsBrowser;
